import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RecordWeekenderPaymentDialog - Extracted from AdminWeekendsTab.js
 * Records manual/offline payments for a booking including:
 * - Payment amount
 * - Payment date
 * - Payment method (cash, card, bank_transfer, cheque, other)
 * - Reference/transaction ID
 * - Notes
 * - Send receipt option
 * 
 * NOTE: This dialog is for MANUAL payment recording only.
 * It does NOT process card payments through Square.
 */
export default function RecordWeekenderPaymentDialog({
  booking,
  open,
  onClose,
  authToken,
  onSuccess
}) {
  const [saving, setSaving] = useState(false);
  
  // Payment form state - mirrors legacy implementation exactly
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: '',
    send_receipt: true,
    payment_date: new Date().toISOString().split('T')[0] // Default to today
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setPaymentForm({
        amount: '',
        payment_method: 'cash',
        reference: '',
        notes: '',
        send_receipt: true,
        payment_date: new Date().toISOString().split('T')[0]
      });
    }
  }, [open]);

  // Handle payment submission
  const handleAddPayment = async () => {
    if (!booking || !paymentForm.amount) {
      toast.error('Please enter a payment amount');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          amount: parseFloat(paymentForm.amount),
          payment_method: paymentForm.payment_method,
          reference: paymentForm.reference || null,
          notes: paymentForm.notes || null,
          send_receipt: paymentForm.send_receipt,
          payment_date: paymentForm.payment_date || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        const receiptMsg = result.receipt_sent ? ' Receipt sent to customer.' : '';
        toast.success(`Payment of £${paymentForm.amount} recorded. Balance: £${result.balance_remaining.toFixed(2)}${receiptMsg}`);
        onClose();
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record payment');
      }
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  const total = booking.total_override || booking.total_amount || booking.total_price || 0;
  const paid = booking.amount_paid || 0;
  const balance = Math.max(0, total - paid);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Record Payment</DialogTitle>
          <DialogDescription className="text-slate-600">
            Add a payment for {booking?.customer_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Balance */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-slate-500">Total Amount:</span>
              <span className="text-right text-slate-900">£{total.toFixed(2)}</span>
              <span className="text-slate-500">Amount Paid:</span>
              <span className="text-right text-green-600">£{paid.toFixed(2)}</span>
              <span className="text-slate-500 font-medium">Balance Due:</span>
              <span className="text-right font-medium text-orange-600">
                £{balance.toFixed(2)}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-slate-700">Payment Amount (£) *</Label>
            <Input
              type="number"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
              min="0.01"
              step="0.01"
              placeholder="0.00"
              className="bg-white border-slate-300"
              data-testid="payment-amount"
            />
          </div>

          <div>
            <Label className="text-slate-700">Payment Date</Label>
            <Input
              type="date"
              value={paymentForm.payment_date}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
              className="bg-white border-slate-300"
              data-testid="payment-date"
            />
            <p className="text-xs text-slate-500 mt-1">Date the payment was received</p>
          </div>

          <div>
            <Label className="text-slate-700">Payment Method</Label>
            <Select
              value={paymentForm.payment_method}
              onValueChange={(val) => setPaymentForm(prev => ({ ...prev, payment_method: val }))}
            >
              <SelectTrigger className="bg-white border-slate-300" data-testid="payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-700">Reference / Transaction ID</Label>
            <Input
              value={paymentForm.reference}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
              placeholder="Optional reference number"
              className="bg-white border-slate-300"
              data-testid="payment-reference"
            />
          </div>

          <div>
            <Label className="text-slate-700">Notes</Label>
            <Textarea
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any notes about this payment..."
              rows={2}
              className="bg-white border-slate-300"
              data-testid="payment-notes"
            />
          </div>

          {/* Send Receipt Checkbox */}
          <div className="flex items-center space-x-2 pt-2">
            <input
              type="checkbox"
              id="send_receipt"
              checked={paymentForm.send_receipt}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, send_receipt: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              data-testid="payment-send-receipt"
            />
            <label htmlFor="send_receipt" className="text-sm text-slate-700">
              Send payment receipt email to customer
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="payment-cancel">
            Cancel
          </Button>
          <Button 
            onClick={handleAddPayment} 
            disabled={saving || !paymentForm.amount}
            data-testid="payment-submit"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
